import { on, off } from 'helpers/dom'
import { useEffect, useRef, useState } from 'preact/hooks'
import Icon from 'snippets/icon/icon'
import LoaderSpin from 'snippets/loader-spin/loader-spin'

function QuantityInput({
  min = 0,
  max = 9999,
  quantity,
  setQuantity,
  disabled = false,
  classWrap = 'bg-white h-[34px] p-0.5 transition-colors duration-300 ease-in-out border-neutral-700',
  classButton = 'w-[30px] h-[30px] p-2',
  isLoading = false,
  title
}) {
  const [value, setValue] = useState(quantity)
  const [isFocused, setIsFocused] = useState(false)
  const [message, setMessage] = useState(null)
  const inputRef = useRef(null)

  const isInRange = (currentValue) =>
    !(currentValue > Number.parseInt(max, 10) || currentValue < Number.parseInt(min, 10))

  const displayMessage = (newValue) => {
    if (newValue > max) {
      setMessage(max > 1 ? `We have only ${max} items` : 'We have only 1 item')
    } else if (newValue < min) {
      setMessage('Enter a positive number')
    }
  }

  const handleChange = (count) => {
    const newValue = value + count

    if (newValue >= min && newValue <= max) {
      setValue(newValue)
      setQuantity(newValue)
      setMessage(null)
    } else {
      displayMessage(newValue)
    }
  }

  const validateInput = (newValue) => /^\d+$/.test(newValue)

  const handleInputChange = (e) => {
    e.preventDefault()

    const currentQuantity = parseInt(e.target.value, 10)

    if (Number.isNaN(currentQuantity)) {
      inputRef.current.value = value

      return
    }

    if (validateInput(currentQuantity)) {
      if (currentQuantity >= min && currentQuantity <= max) {
        setValue(currentQuantity)
        setQuantity(currentQuantity)
        setMessage(null)
      } else {
        displayMessage(currentQuantity)
      }
    } else {
      setMessage('Enter only positive numbers')
    }

    setIsFocused(false)
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  useEffect(() => {
    if (value > max) setValue(Number.parseInt(max, 10))
    if (value < min) setValue(Number.parseInt(min, 10))

    if (isInRange(value)) {
      setQuantity(value)
    }
  }, [value])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        handleInputChange(event)
      }
    }

    const currentInputRef = inputRef.current

    if (currentInputRef) {
      on('keydown', handleKeyDown, currentInputRef)
    }

    return () => {
      if (currentInputRef) {
        off('keydown', handleKeyDown, currentInputRef)
      }
    }
  }, [isLoading])

  return (
    <>
      <div
        className={`relative flex items-center justify-between overflow-hidden border ${classWrap} ${isFocused ? 'border-neutral-900' : 'hover:border-neutral-300'}`}
      >
        <button
          type="button"
          className={`group flex shrink-0 items-center justify-center text-neutral-700 hover:bg-neutral-800 focus:bg-neutral-800 ${classButton} ${
            value <= min || disabled || isLoading ? 'pointer-events-none cursor-not-allowed opacity-50' : ''
          }`}
          disabled={value <= min || disabled || isLoading}
          onClick={() => handleChange(-1)}
          aria-label={`Decrease quantity${title && ` for ${title}`}`}
        >
          <Icon
            viewBox="0 0 24 24"
            name="icon-minus"
            className="h-3.5 w-3.5 text-neutral-700 group-hover:text-white group-focus:text-white"
          />
        </button>
        <div className="relative w-7 text-center">
          {!isLoading ? (
            <input
              type="number"
              ref={inputRef}
              value={value}
              className="text-xs w-7 h-7 appearance-none border-none bg-transparent p-0 text-center text-neutral-900 !shadow-none !ring-0 [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              min={min}
              max={max}
              onChange={handleInputChange}
              onBlur={handleInputChange}
              readOnly={disabled}
              aria-label="Quantity"
              onFocus={handleFocus}
            />
          ) : (
            <LoaderSpin size="4" className="mx-auto" />
          )}
        </div>
        <button
          type="button"
          className={`group flex shrink-0 items-center justify-center text-neutral-700 hover:bg-neutral-800 focus:bg-neutral-800 ${classButton} ${
            value >= max || disabled || isLoading ? 'pointer-events-none cursor-not-allowed opacity-50' : ''
          }`}
          disabled={value >= max || disabled || isLoading}
          onClick={() => handleChange(1)}
          aria-label={`Increase quantity${title && ` for ${title}`}`}
        >
          <Icon
            viewBox="0 0 24 24"
            name="icon-plus"
            className="h-3.5 w-3.5 text-neutral-700 group-hover:text-white group-focus:text-white"
          />
        </button>
      </div>
      {message && <span className="text-xs-medium mt-1 block text-error-content lg:text-xs">{message}</span>}
    </>
  )
}

export default QuantityInput
